import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';

import { SupabaseService } from '../../../core/services/supabase.service';
import { WellbeingRepository } from '../domain/wellbeing.repository';
import { SatisfactionScore } from '../models/satisfaction.model';

function isSatisfactionScore(value: number): value is SatisfactionScore {
  return Number.isInteger(value) && value >= -3 && value <= 3;
}

@Injectable()
export class SupabaseWellbeingRepository extends WellbeingRepository {
  // Generated Supabase types require owner_id on inserts and mark nullable SQL RPC
  // parameters as non-null. The database owns those invariants, so this adapter keeps
  // the SDK client broad while its mapped rows remain explicitly typed.
  private readonly client: SupabaseClient;

  constructor(supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  async loadScore(date: string): Promise<SatisfactionScore | null> {
    const { data, error } = await this.client
      .from('daily_satisfaction')
      .select('id,score')
      .eq('satisfaction_date', date)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;

    const score = Number(data.score);
    if (!isSatisfactionScore(score)) {
      throw new Error(`Invalid satisfaction score returned by persistence: ${data.score}`);
    }
    return score;
  }

  async saveScore(date: string, score: SatisfactionScore): Promise<void> {
    const { data: existing, error: lookupError } = await this.client
      .from('daily_satisfaction')
      .select('id')
      .eq('satisfaction_date', date)
      .maybeSingle();
    if (lookupError) throw lookupError;

    const query = existing
      ? this.client
          .from('daily_satisfaction')
          .update({ score })
          .eq('id', existing.id)
      : this.client
          .from('daily_satisfaction')
          .insert({ satisfaction_date: date, score });

    const { error } = await query;
    if (error) throw error;
  }

  async clearScore(date: string): Promise<void> {
    const { error } = await this.client
      .from('daily_satisfaction')
      .delete()
      .eq('satisfaction_date', date);
    if (error) throw error;
  }
}
